import { CUSTOM_APP_LOCALES_MAP, DEFAULT_LOCALE } from 'src/constants/locales.const';
import { intlGetMessagesLocale } from 'src/utils/intl';

/**
 * UI-locale glosses for celebration clip phrases.
 * Keys are the English `translation` strings from celebrationVideos.ts.
 * Locales match files under locales/ (TRANSLATED_LOCALES).
 */
const CELEBRATION_UI_LOCALES = [
  'ar',
  'de',
  'en',
  'es',
  'es-mx',
  'fa',
  'fr',
  'hi',
  'id',
  'it',
  'ja',
  'ko',
  'nl',
  'no',
  'pl',
  'pt',
  'pt-br',
  'ru',
  'sv',
  'tr',
  'vi',
  'zh-cn',
  'zh-hant',
] as const;

type TranslatedLocale = (typeof CELEBRATION_UI_LOCALES)[number];

const CELEBRATION_TRANSLATIONS: Record<string, Record<TranslatedLocale, string>> = {
  'Absolutely amazing! (slang)': {
    ar: 'مذهل تمامًا! (عامية)',
    de: 'Total genial! (Slang)',
    en: 'Absolutely amazing! (slang)',
    es: '¡Absolutamente genial! (jerga)',
    'es-mx': '¡Súper genial! (jerga)',
    fa: 'فوق‌العاده! (عامیانه)',
    fr: 'Carrément génial ! (familier)',
    hi: 'बिल्कुल शानदार! (स्लैंग)',
    id: 'Mantul banget! (slang)',
    it: 'Assolutamente fantastico! (slang)',
    ja: 'めっちゃすごい！（スラング）',
    ko: '완전 대박! (속어)',
    nl: 'Absoluut geweldig! (slang)',
    no: 'Absolutt fantastisk! (slang)',
    pl: 'Absolutnie niesamowite! (slang)',
    pt: 'Absolutamente incrível! (calão)',
    'pt-br': 'Absolutamente incrível! (gíria)',
    ru: 'Просто огонь! (сленг)',
    sv: 'Absolut fantastiskt! (slang)',
    tr: 'Kesinlikle harika! (argo)',
    vi: 'Tuyệt vời hết sức! (lóng)',
    'zh-cn': '太牛了！（俚语）',
    'zh-hant': '太猛了！（俚語）',
  },
  Amazing: {
    ar: 'مذهل',
    de: 'Unglaublich',
    en: 'Amazing',
    es: 'Increíble',
    'es-mx': 'Increíble',
    fa: 'شگفت‌انگیز',
    fr: 'Incroyable',
    hi: 'अद्भुत',
    id: 'Luar biasa',
    it: 'Incredibile',
    ja: 'すごい',
    ko: '대단해요',
    nl: 'Geweldig',
    no: 'Utrolig',
    pl: 'Niesamowite',
    pt: 'Incrível',
    'pt-br': 'Incrível',
    ru: 'Потрясающе',
    sv: 'Otroligt',
    tr: 'İnanılmaz',
    vi: 'Tuyệt vời',
    'zh-cn': '太惊人了',
    'zh-hant': '太驚人了',
  },
  'Amazing!': {
    ar: 'مذهل!',
    de: 'Unglaublich!',
    en: 'Amazing!',
    es: '¡Increíble!',
    'es-mx': '¡Increíble!',
    fa: 'شگفت‌انگیز!',
    fr: 'Incroyable !',
    hi: 'अद्भुत!',
    id: 'Luar biasa!',
    it: 'Incredibile!',
    ja: 'すごい！',
    ko: '대단해요!',
    nl: 'Geweldig!',
    no: 'Utrolig!',
    pl: 'Niesamowite!',
    pt: 'Incrível!',
    'pt-br': 'Incrível!',
    ru: 'Потрясающе!',
    sv: 'Otroligt!',
    tr: 'İnanılmaz!',
    vi: 'Tuyệt vời!',
    'zh-cn': '太惊人了！',
    'zh-hant': '太驚人了！',
  },
  Awesome: {
    ar: 'رائع',
    de: 'Toll',
    en: 'Awesome',
    es: 'Genial',
    'es-mx': 'Genial',
    fa: 'عالی',
    fr: 'Génial',
    hi: 'शानदार',
    id: 'Keren',
    it: 'Fantastico',
    ja: 'すごい',
    ko: '멋져요',
    nl: 'Geweldig',
    no: 'Kjempebra',
    pl: 'Świetnie',
    pt: 'Incrível',
    'pt-br': 'Incrível',
    ru: 'Круто',
    sv: 'Fantastiskt',
    tr: 'Harika',
    vi: 'Tuyệt vời',
    'zh-cn': '太棒了',
    'zh-hant': '太棒了',
  },
  'Awesome!': {
    ar: 'رائع!',
    de: 'Toll!',
    en: 'Awesome!',
    es: '¡Genial!',
    'es-mx': '¡Genial!',
    fa: 'عالی!',
    fr: 'Génial !',
    hi: 'शानदार!',
    id: 'Keren!',
    it: 'Fantastico!',
    ja: 'すごい！',
    ko: '멋져요!',
    nl: 'Geweldig!',
    no: 'Kjempebra!',
    pl: 'Świetnie!',
    pt: 'Incrível!',
    'pt-br': 'Incrível!',
    ru: 'Круто!',
    sv: 'Fantastiskt!',
    tr: 'Harika!',
    vi: 'Tuyệt vời!',
    'zh-cn': '太棒了！',
    'zh-hant': '太棒了！',
  },
  Congratulations: {
    ar: 'تهانينا',
    de: 'Herzlichen Glückwunsch',
    en: 'Congratulations',
    es: 'Enhorabuena',
    'es-mx': 'Felicidades',
    fa: 'تبریک',
    fr: 'Félicitations',
    hi: 'बधाई हो',
    id: 'Selamat',
    it: 'Congratulazioni',
    ja: 'おめでとう',
    ko: '축하해요',
    nl: 'Gefeliciteerd',
    no: 'Gratulerer',
    pl: 'Gratulacje',
    pt: 'Parabéns',
    'pt-br': 'Parabéns',
    ru: 'Поздравляем',
    sv: 'Grattis',
    tr: 'Tebrikler',
    vi: 'Xin chúc mừng',
    'zh-cn': '恭喜',
    'zh-hant': '恭喜',
  },
  'Congratulations!': {
    ar: 'تهانينا!',
    de: 'Herzlichen Glückwunsch!',
    en: 'Congratulations!',
    es: '¡Enhorabuena!',
    'es-mx': '¡Felicidades!',
    fa: 'تبریک!',
    fr: 'Félicitations !',
    hi: 'बधाई हो!',
    id: 'Selamat!',
    it: 'Congratulazioni!',
    ja: 'おめでとうございます！',
    ko: '축하해요!',
    nl: 'Gefeliciteerd!',
    no: 'Gratulerer!',
    pl: 'Gratulacje!',
    pt: 'Parabéns!',
    'pt-br': 'Parabéns!',
    ru: 'Поздравляем!',
    sv: 'Grattis!',
    tr: 'Tebrikler!',
    vi: 'Xin chúc mừng!',
    'zh-cn': '恭喜！',
    'zh-hant': '恭喜！',
  },
  Excellent: {
    ar: 'ممتاز',
    de: 'Ausgezeichnet',
    en: 'Excellent',
    es: 'Excelente',
    'es-mx': 'Excelente',
    fa: 'عالی',
    fr: 'Excellent',
    hi: 'उत्कृष्ट',
    id: 'Luar biasa',
    it: 'Eccellente',
    ja: '素晴らしい',
    ko: '훌륭해요',
    nl: 'Uitstekend',
    no: 'Utmerket',
    pl: 'Doskonale',
    pt: 'Excelente',
    'pt-br': 'Excelente',
    ru: 'Отлично',
    sv: 'Utmärkt',
    tr: 'Mükemmel',
    vi: 'Xuất sắc',
    'zh-cn': '太好了',
    'zh-hant': '太好了',
  },
  'Excellent!': {
    ar: 'ممتاز!',
    de: 'Ausgezeichnet!',
    en: 'Excellent!',
    es: '¡Excelente!',
    'es-mx': '¡Excelente!',
    fa: 'عالی!',
    fr: 'Excellent !',
    hi: 'उत्कृष्ट!',
    id: 'Luar biasa!',
    it: 'Eccellente!',
    ja: '素晴らしい！',
    ko: '훌륭해요!',
    nl: 'Uitstekend!',
    no: 'Utmerket!',
    pl: 'Doskonale!',
    pt: 'Excelente!',
    'pt-br': 'Excelente!',
    ru: 'Отлично!',
    sv: 'Utmärkt!',
    tr: 'Mükemmel!',
    vi: 'Xuất sắc!',
    'zh-cn': '太好了！',
    'zh-hant': '太好了！',
  },
  'Fantastic!': {
    ar: 'رائع!',
    de: 'Fantastisch!',
    en: 'Fantastic!',
    es: '¡Fantástico!',
    'es-mx': '¡Fantástico!',
    fa: 'فوق‌العاده!',
    fr: 'Fantastique !',
    hi: 'शानदार!',
    id: 'Fantastis!',
    it: 'Fantastico!',
    ja: '素晴らしい！',
    ko: '환상적이에요!',
    nl: 'Fantastisch!',
    no: 'Fantastisk!',
    pl: 'Fantastycznie!',
    pt: 'Fantástico!',
    'pt-br': 'Fantástico!',
    ru: 'Фантастика!',
    sv: 'Fantastiskt!',
    tr: 'Harika!',
    vi: 'Tuyệt vời!',
    'zh-cn': '太棒了！',
    'zh-hant': '太棒了！',
  },
  'Good job!': {
    ar: 'عمل رائع!',
    de: 'Gute Arbeit!',
    en: 'Good job!',
    es: '¡Buen trabajo!',
    'es-mx': '¡Buen trabajo!',
    fa: 'آفرین!',
    fr: 'Bon travail !',
    hi: 'शाबाश!',
    id: 'Kerja bagus!',
    it: 'Ottimo lavoro!',
    ja: 'よくやった！',
    ko: '잘했어요!',
    nl: 'Goed gedaan!',
    no: 'Bra jobbet!',
    pl: 'Dobra robota!',
    pt: 'Bom trabalho!',
    'pt-br': 'Bom trabalho!',
    ru: 'Отличная работа!',
    sv: 'Bra jobbat!',
    tr: 'İyi iş!',
    vi: 'Làm tốt lắm!',
    'zh-cn': '干得好！',
    'zh-hant': '幹得好！',
  },
  "It's very good!": {
    ar: 'إنه جيد جدًا!',
    de: 'Das ist sehr gut!',
    en: "It's very good!",
    es: '¡Está muy bien!',
    'es-mx': '¡Está muy bien!',
    fa: 'خیلی خوبه!',
    fr: "C'est très bien !",
    hi: 'यह बहुत अच्छा है!',
    id: 'Sangat bagus!',
    it: 'È molto bene!',
    ja: 'とても良いです！',
    ko: '아주 좋아요!',
    nl: 'Het is heel goed!',
    no: 'Det er veldig bra!',
    pl: 'Jest bardzo dobrze!',
    pt: 'Está muito bem!',
    'pt-br': 'Está muito bem!',
    ru: 'Очень хорошо!',
    sv: 'Det är jättebra!',
    tr: 'Çok iyi!',
    vi: 'Rất tốt!',
    'zh-cn': '非常好！',
    'zh-hant': '非常好！',
  },
  'Keep it up!': {
    ar: 'واصل على هذا النحو!',
    de: 'Weiter so!',
    en: 'Keep it up!',
    es: '¡Sigue así!',
    'es-mx': '¡Sigue así!',
    fa: 'همین‌طور ادامه بده!',
    fr: 'Continue comme ça !',
    hi: 'ऐसे ही जारी रखो!',
    id: 'Terus begitu!',
    it: 'Continua così!',
    ja: 'その調子！',
    ko: '계속해요!',
    nl: 'Ga zo door!',
    no: 'Fortsett sånn!',
    pl: 'Tak trzymaj!',
    pt: 'Continua assim!',
    'pt-br': 'Continue assim!',
    ru: 'Так держать!',
    sv: 'Fortsätt så!',
    tr: 'Böyle devam!',
    vi: 'Cứ thế nhé!',
    'zh-cn': '继续加油！',
    'zh-hant': '繼續加油！',
  },
  'My congratulations!': {
    ar: 'تهانينا مني!',
    de: 'Meine Glückwünsche!',
    en: 'My congratulations!',
    es: '¡Mis felicitaciones!',
    'es-mx': '¡Mis felicidades!',
    fa: 'تبریک‌های من!',
    fr: 'Toutes mes félicitations !',
    hi: 'मेरी बधाई!',
    id: 'Selamat dari saya!',
    it: 'I miei complimenti!',
    ja: 'おめでとうございます！',
    ko: '축하드립니다!',
    nl: 'Mijn felicitaties!',
    no: 'Mine gratulasjoner!',
    pl: 'Moje gratulacje!',
    pt: 'Os meus parabéns!',
    'pt-br': 'Meus parabéns!',
    ru: 'Мои поздравления!',
    sv: 'Mina gratulationer!',
    tr: 'Tebriklerim!',
    vi: 'Xin chúc mừng bạn!',
    'zh-cn': '恭喜你！',
    'zh-hant': '恭喜你！',
  },
  "That's great!": {
    ar: 'هذا رائع!',
    de: 'Das ist toll!',
    en: "That's great!",
    es: '¡Eso es genial!',
    'es-mx': '¡Eso es genial!',
    fa: 'عالی است!',
    fr: "C'est génial !",
    hi: 'यह शानदार है!',
    id: 'Itu keren!',
    it: 'È fantastico!',
    ja: 'すごいですね！',
    ko: '멋져요!',
    nl: 'Dat is geweldig!',
    no: 'Det er kjempebra!',
    pl: 'To świetnie!',
    pt: 'Isso é ótimo!',
    'pt-br': 'Isso é ótimo!',
    ru: 'Это здорово!',
    sv: 'Det är fantastiskt!',
    tr: 'Bu harika!',
    vi: 'Thật tuyệt!',
    'zh-cn': '太好了！',
    'zh-hant': '太好了！',
  },
  'Very good!': {
    ar: 'جيد جدًا!',
    de: 'Sehr gut!',
    en: 'Very good!',
    es: '¡Muy bien!',
    'es-mx': '¡Muy bien!',
    fa: 'خیلی خوب!',
    fr: 'Très bien !',
    hi: 'बहुत अच्छा!',
    id: 'Sangat bagus!',
    it: 'Molto bene!',
    ja: 'とても良い！',
    ko: '아주 좋아요!',
    nl: 'Heel goed!',
    no: 'Veldig bra!',
    pl: 'Bardzo dobrze!',
    pt: 'Muito bem!',
    'pt-br': 'Muito bem!',
    ru: 'Очень хорошо!',
    sv: 'Mycket bra!',
    tr: 'Çok iyi!',
    vi: 'Rất tốt!',
    'zh-cn': '非常好！',
    'zh-hant': '非常好！',
  },
  'Very well': {
    ar: 'جيد جدًا',
    de: 'Sehr gut',
    en: 'Very well',
    es: 'Muy bien',
    'es-mx': 'Muy bien',
    fa: 'خیلی خوب',
    fr: 'Très bien',
    hi: 'बहुत अच्छा',
    id: 'Sangat bagus',
    it: 'Molto bene',
    ja: 'とても良い',
    ko: '아주 좋아요',
    nl: 'Heel goed',
    no: 'Veldig bra',
    pl: 'Bardzo dobrze',
    pt: 'Muito bem',
    'pt-br': 'Muito bem',
    ru: 'Очень хорошо',
    sv: 'Mycket bra',
    tr: 'Çok iyi',
    vi: 'Rất tốt',
    'zh-cn': '非常好',
    'zh-hant': '非常好',
  },
  'Well done': {
    ar: 'أحسنت',
    de: 'Gut gemacht',
    en: 'Well done',
    es: 'Bien hecho',
    'es-mx': 'Bien hecho',
    fa: 'آفرین',
    fr: 'Bravo',
    hi: 'शाबाश',
    id: 'Bagus',
    it: 'Ben fatto',
    ja: 'よくできました',
    ko: '잘했어요',
    nl: 'Goed gedaan',
    no: 'Bra jobbet',
    pl: 'Dobra robota',
    pt: 'Muito bem',
    'pt-br': 'Muito bem',
    ru: 'Отлично',
    sv: 'Bra jobbat',
    tr: 'Aferin',
    vi: 'Làm tốt lắm',
    'zh-cn': '干得漂亮',
    'zh-hant': '做得好',
  },
  'Well done!': {
    ar: 'أحسنت!',
    de: 'Gut gemacht!',
    en: 'Well done!',
    es: '¡Bien hecho!',
    'es-mx': '¡Bien hecho!',
    fa: 'آفرین!',
    fr: 'Bravo !',
    hi: 'शाबाश!',
    id: 'Bagus!',
    it: 'Ben fatto!',
    ja: 'よくできました！',
    ko: '잘했어요!',
    nl: 'Goed gedaan!',
    no: 'Bra jobbet!',
    pl: 'Dobra robota!',
    pt: 'Muito bem!',
    'pt-br': 'Muito bem!',
    ru: 'Отлично!',
    sv: 'Bra jobbat!',
    tr: 'Aferin!',
    vi: 'Làm tốt lắm!',
    'zh-cn': '干得漂亮！',
    'zh-hant': '做得好！',
  },
  'Well played!': {
    ar: 'لعب رائع!',
    de: 'Gut gespielt!',
    en: 'Well played!',
    es: '¡Bien jugado!',
    'es-mx': '¡Bien jugado!',
    fa: 'آفرین!',
    fr: 'Bien joué !',
    hi: 'शाबाश!',
    id: 'Bagus!',
    it: 'Ben giocato!',
    ja: 'お見事！',
    ko: '잘했어요!',
    nl: 'Goed gespeeld!',
    no: 'Bra spilt!',
    pl: 'Dobrze zagrane!',
    pt: 'Bem jogado!',
    'pt-br': 'Bem jogado!',
    ru: 'Хорошо сыграно!',
    sv: 'Bra spelat!',
    tr: 'İyi oynadın!',
    vi: 'Chơi hay lắm!',
    'zh-cn': '干得漂亮！',
    'zh-hant': '幹得漂亮！',
  },
  'You speak Polish very well!': {
    ar: 'أنت تتحدث البولندية جيدًا جدًا!',
    de: 'Du sprichst sehr gut Polnisch!',
    en: 'You speak Polish very well!',
    es: '¡Hablas muy bien polaco!',
    'es-mx': '¡Hablas muy bien polaco!',
    fa: 'لهستانی را خیلی خوب صحبت می‌کنی!',
    fr: 'Tu parles très bien le polonais !',
    hi: 'आप बहुत अच्छा पोलिश बोलते हैं!',
    id: 'Kamu berbicara bahasa Polandia dengan sangat baik!',
    it: 'Parli molto bene il polacco!',
    ja: 'ポーランド語がとても上手ですね！',
    ko: '폴란드어를 아주 잘하시네요!',
    nl: 'Je spreekt heel goed Pools!',
    no: 'Du snakker polsk veldig bra!',
    pl: 'Mówisz bardzo dobrze po polsku!',
    pt: 'Falas muito bem polaco!',
    'pt-br': 'Você fala polonês muito bem!',
    ru: 'Ты очень хорошо говоришь по-польски!',
    sv: 'Du talar väldigt bra polska!',
    tr: 'Lehçeyi çok iyi konuşuyorsun!',
    vi: 'Bạn nói tiếng Ba Lan rất giỏi!',
    'zh-cn': '你的波兰语说得很好！',
    'zh-hant': '你的波蘭語說得很好！',
  },
  'You speak Portuguese very well!': {
    ar: 'أنت تتحدث البرتغالية جيدًا جدًا!',
    de: 'Du sprichst sehr gut Portugiesisch!',
    en: 'You speak Portuguese very well!',
    es: '¡Hablas muy bien portugués!',
    'es-mx': '¡Hablas muy bien portugués!',
    fa: 'پرتغالی را خیلی خوب صحبت می‌کنی!',
    fr: 'Tu parles très bien le portugais !',
    hi: 'आप बहुत अच्छा पुर्तगाली बोलते हैं!',
    id: 'Kamu berbicara bahasa Portugis dengan sangat baik!',
    it: 'Parli molto bene il portoghese!',
    ja: 'ポルトガル語がとても上手ですね！',
    ko: '포르투갈어를 아주 잘하시네요!',
    nl: 'Je spreekt heel goed Portugees!',
    no: 'Du snakker portugisisk veldig bra!',
    pl: 'Mówisz bardzo dobrze po portugalsku!',
    pt: 'Falas muito bem português!',
    'pt-br': 'Você fala português muito bem!',
    ru: 'Ты очень хорошо говоришь по-португальски!',
    sv: 'Du talar väldigt bra portugisiska!',
    tr: 'Portekizceyi çok iyi konuşuyorsun!',
    vi: 'Bạn nói tiếng Bồ Đào Nha rất giỏi!',
    'zh-cn': '你的葡萄牙语说得很好！',
    'zh-hant': '你的葡萄牙語說得很好！',
  },
  "You're awesome!": {
    ar: 'أنت رائع!',
    de: 'Du bist toll!',
    en: "You're awesome!",
    es: '¡Eres genial!',
    'es-mx': '¡Eres genial!',
    fa: 'تو عالی هستی!',
    fr: 'Tu es génial !',
    hi: 'आप शानदार हैं!',
    id: 'Kamu keren!',
    it: 'Sei fantastico!',
    ja: 'あなたはすごい！',
    ko: '멋져요!',
    nl: 'Je bent geweldig!',
    no: 'Du er kjempebra!',
    pl: 'Jesteś niesamowity!',
    pt: 'És incrível!',
    'pt-br': 'Você é incrível!',
    ru: 'Ты крутой!',
    sv: 'Du är fantastisk!',
    tr: 'Harikasın!',
    vi: 'Bạn thật tuyệt!',
    'zh-cn': '你太棒了！',
    'zh-hant': '你太棒了！',
  },
};

const isTranslatedLocale = (locale: string): locale is TranslatedLocale =>
  (CELEBRATION_UI_LOCALES as readonly string[]).includes(locale);

export const resolveCelebrationUiLocale = (locale: string): TranslatedLocale => {
  const messagesLocale = intlGetMessagesLocale(locale).toLowerCase();
  if (isTranslatedLocale(messagesLocale)) {
    return messagesLocale;
  }

  const mapped = CUSTOM_APP_LOCALES_MAP[messagesLocale]?.toLowerCase();
  if (mapped && isTranslatedLocale(mapped)) {
    return mapped;
  }

  const base = messagesLocale.split('-')[0];
  if (isTranslatedLocale(base)) {
    return base;
  }

  return DEFAULT_LOCALE as TranslatedLocale;
};

/** Localize a celebration clip English gloss into the learner's UI language. */
export const localizeCelebrationTranslation = (
  englishTranslation: string,
  uiLocale: string,
): string => {
  const table = CELEBRATION_TRANSLATIONS[englishTranslation];
  if (!table) {
    return englishTranslation;
  }

  const locale = resolveCelebrationUiLocale(uiLocale);
  return table[locale] ?? table.en ?? englishTranslation;
};
